import React from "react";
import { Alert, Box, Paper, Stack, Typography } from "@mui/material";
import type { SpecsValidationIssue } from "../../../service/SpecsRevisionService";
import { createStyles, spacing } from "../../../styles/theme";
import { useSpecsPageContext } from "../SpecsPageContext";
import { SpecsFabricsModel } from "../specsModel/SpecsFabricsModel";
import { JsonEditor } from "./FabricsSpecsTableEditorFields";
import { SpecsValidationIssueShell } from "../specsForm/SpecsValidationIssueHighlight";
import { getSpecsIssueMatchForModelPath } from "../specsValidationIssues";
import {
  FabricColorsSection,
  FabricDefaultColorSection,
} from "./FabricColorsTable";
import { FabricRootTable } from "./FabricRootTable";
import { setValueAtModelPath } from "../specsForm/modelPath";

const styles = createStyles({
  paper: {
    p: spacing.groupingDesktop,
  },
  centeredSection: {
    width: "100%",
    maxWidth: 1200,
    marginLeft: "auto !important",
    marginRight: "auto !important",
  },
});

export function FabricsSpecsTableForm({
  model,
  description,
  validationIssues = [],
}: {
  model: SpecsFabricsModel;
  description: string;
  validationIssues?: SpecsValidationIssue[];
}) {
  const getIssueMatch = (modelPath: string) =>
    getSpecsIssueMatchForModelPath({
      issues: validationIssues,
      specType: "fabric",
      modelPath,
    });

  return (
    <Paper variant="outlined" sx={styles.paper}>
      <Stack spacing={spacing.groupingDesktop}>
        {description ? (
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        ) : null}

        <Box sx={styles.centeredSection}>
          <Typography variant="h2" sx={{ mb: 1 }}>
            Fabric
          </Typography>
          <FabricRootTable model={model} validationIssues={validationIssues} />
        </Box>

        <FabricDefaultColorSection
          model={model}
          validationIssues={validationIssues}
        />

        <FabricColorsSection
          model={model}
          validationIssues={validationIssues}
        />

        <Box sx={styles.centeredSection}>
          <SpecsValidationIssueShell match={getIssueMatch("fabric.extra")}>
            <Typography variant="h2" sx={{ mb: 1 }}>
              Fabric Extra JSON
            </Typography>
            <JsonEditor
              value={model.fabric.extra}
              onChange={(nextValue) =>
                setValueAtModelPath(model, "fabric.extra", nextValue)
              }
              minRows={6}
            />
          </SpecsValidationIssueShell>
        </Box>
      </Stack>
    </Paper>
  );
}

export default function FabricsSpecsTableEditor() {
  const { description, emptyMessage, draftModel, validationResult } =
    useSpecsPageContext();

  if (!(draftModel instanceof SpecsFabricsModel)) {
    return <Alert severity="info">{emptyMessage}</Alert>;
  }

  return (
    <FabricsSpecsTableForm
      model={draftModel}
      description={description}
      validationIssues={validationResult?.valid ? [] : validationResult?.issues}
    />
  );
}
